import React, { memo, useEffect, useMemo, useState } from "react";
import { isEqual } from "lodash";

//* ReactFlow Library
import { Handle, Position, useReactFlow } from "reactflow";

//* Node Utils
import nodeUtils from "../utilities/nodeUtils";

//* Import Stores
import {
  useResetStore,
  useDuplicateGameStore,
} from "../../../utilities/stores";

const CompassNode = ({ data, id, selected }) => {
  const { getNode } = useReactFlow();
  const { formReset, setFormReset } = useResetStore();
  const { duplicateGames } = useDuplicateGameStore();
  const {
    labels,
    gameLists,
    updateNodeData,
    updateUsedTitles,
    updateDuplicateGames,
    deleteSingleNode,
  } = nodeUtils();

  const defaultInputValue = {
    team_1_of_type: data.team_1_of_type ?? "",
    team_1_of_type_label: data.team_1_of_type_label ?? "",
    team_1_of_type_value: data.team_1_of_type_value ?? "",
    team_2_of_type: data.team_2_of_type ?? "",
    team_2_of_type_label: data.team_2_of_type_label ?? "",
    team_2_of_type_value: data.team_2_of_type_value ?? "",
    team_3_of_type: data.team_3_of_type ?? "",
    team_3_of_type_label: data.team_3_of_type_label ?? "",
    team_3_of_type_value: data.team_3_of_type_value ?? "",
    game_number: data.game_number ?? 0,
  };

  const [inputValues, setInputValues] = useState(defaultInputValue);

  const invalidTeam = useMemo(() => {
    const isEqual =
      data.team_1_of_type &&
      data.team_2_of_type &&
      data.team_1_of_type == data.team_2_of_type &&
      data.team_1_of_type_value == data.team_2_of_type_value;

    return isEqual;
  }, [data]);

  useEffect(() => {
    if (data.newGenerated != undefined && data.newGenerated) {
      setInputValues((prev) => ({
        ...prev,
        team_1_of_type: data.team_1_of_type ?? prev.team_1_of_type,
        team_1_of_type_label:
          data.team_1_of_type_label ?? prev.team_1_of_type_label,
        team_1_of_type_value:
          data.team_1_of_type_value ?? prev.team_1_of_type_value,
        team_2_of_type: data.team_2_of_type ?? prev.team_2_of_type,
        team_2_of_type_label:
          data.team_2_of_type_label ?? prev.team_2_of_type_label,
        team_2_of_type_value:
          data.team_2_of_type_value ?? prev.team_2_of_type_value,
      }));
    }
  }, [data]);

  //* Add the team rank
  let team1Value, team2Value;
  if (data.isFront && data.hideSeed) {
    team1Value =
      data.team_1_rank > 0 && data.team_1_of_type_label
        ? `[${data.team_1_rank}] ${data.team_1_of_type_label}`
        : data.team_1_of_type_label
        ? data.team_1_of_type_label
        : "";
    team2Value =
      data.team_2_rank > 0 && data.team_2_of_type_label
        ? `[${data.team_2_rank}] ${data.team_2_of_type_label}`
        : data.team_2_of_type_label
        ? data.team_2_of_type_label
        : "";
  }

  //* Manage Reset State
  useEffect(() => {
    if (formReset && !isEqual(defaultInputValue, inputValues)) {
      setInputValues(defaultInputValue);
    }
    setFormReset(false);
  }, [formReset]);

  //* On input change to trigger
  const onGameChange = async (e) => {
    const { name, value } = e.target;
    await setInputValues((prev) => ({ ...prev, [name]: value }));
    await updateNodeData(id, { [name]: value });
    await updateDuplicateGames();
  };

  //* On Text drop into placeholder
  const onDrop = async (event) => {
    event.preventDefault();
    const teamVal = event.dataTransfer.getData("teamData");
    if (teamVal) {
      const fieldValue = JSON.parse(teamVal);
      const fieldName = event.target.getAttribute("name");

      // Drop validation for the working team.
      if (
        fieldName == "team_3_of_type" &&
        ((fieldValue.type == inputValues.team_1_of_type &&
          fieldValue.value == inputValues.team_1_of_type_value) ||
          (fieldValue.type == inputValues.team_2_of_type &&
            fieldValue.value == inputValues.team_2_of_type_value))
      ) {
        return false;
      }

      if (fieldValue && fieldName != "game_number") {
        await setInputValues((prev) => ({
          ...prev,
          [fieldName]: fieldValue.type,
          [`${fieldName}_value`]: fieldValue.value,
          [`${fieldName}_label`]: fieldValue.label,
        }));

        await updateNodeData(id, {
          [fieldName]: fieldValue.type,
          [`${fieldName}_value`]: fieldValue.value,
          [`${fieldName}_label`]: fieldValue.label,
        });

        //* Reload the dropped nodes
        const node = getNode(id);
        if (!isEqual(node.data, data)) {
          await updateUsedTitles();
        }
      }
    }
  };

  //* On Edge Connect
  const onEdgeConnect = async (params) => {
    const sourceNode = getNode(params.source);
    const targetNode = getNode(params.target);

    const handle = params.targetHandle == "target-a" ? "team_1" : "team_2";
    const label = params.sourceHandle == "source-a" ? "loser" : "winner";

    if (targetNode.data[`${handle}_of_type`] != "team") {
      const newVal = {
        [`${handle}_of_type`]: label,
        [`${handle}_of_type_value`]: sourceNode.data.game_number,
        [`${handle}_of_type_label`]: `${labels[label]} ${sourceNode.data.game_number}`,
        newGenerated: true,
      };
      await updateNodeData(targetNode.id, newVal);
      await updateUsedTitles();
    }
  };

  //* On close button
  const onRemoveValue = async (team) => {
    const removeVal = {
      [`team_${team}_of_type`]: "",
      [`team_${team}_of_type_label`]: "",
      [`team_${team}_of_type_value`]: "",
    };

    await setInputValues((prev) => ({
      ...prev,
      ...removeVal,
    }));

    await updateNodeData(id, {
      ...removeVal,
      [`team_${team}_score`]: "00",
    });

    await updateUsedTitles();
  };

  //* On remove node
  const onRemoveNode = async (id) => {
    await deleteSingleNode(id);
    await updateUsedTitles();
  };

  const onCustomValue = (event, team) => {
    const value = event.target.value;
    const customVal = {
      [`team_${team}_of_type`]: "custom",
      [`team_${team}_of_type_value`]: value,
      [`team_${team}_of_type_label`]: value,
    };

    setInputValues((prev) => ({
      ...prev,
      ...customVal,
    }));

    updateNodeData(id, customVal);
  };

  return (
    <div
      className={`parent-close-container customNode${
        !data.isFront && selected ? " selected" : ""
      } ${duplicateGames[id] ? " duplicate" : ""} ${
        invalidTeam ? "invalidTeam" : ""
      } ${data.hasWorkingTeam ? "p-5-20" : ""}`.trim()}
    >
      {!data.isFront && (
        <div className="parent-close">
          <button
            type="button"
            className="close"
            onClick={() => onRemoveNode(id)}
            aria-label="Close"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
      )}
      <div className="row" onDrop={onDrop}>
        <div className={`col-md-${data.isFront ? "3" : "4"}`}>
          <select
            name="game_number"
            className="form-control"
            onChange={onGameChange}
            value={inputValues.game_number}
            id={`game_number-${id}`}
            disabled={data.isFront}
          >
            {!data.isFront ? (
              gameLists
            ) : (
              <option value={inputValues.game_number}>
                {inputValues.game_number}
              </option>
            )}
          </select>
        </div>
        <div className={`text-right col-md-${data.isFront ? "9" : "8"}`}>
          <span>
            <i>
              {data.schedule_game_id ? (
                data.field_name ? (
                  data.field_name
                ) : (
                  "TBD"
                )
              ) : (
                <>&nbsp;</>
              )}
            </i>
          </span>
          <br />
          <span>
            <i>
              {data.schedule_game_id ? (
                data.schedule_time ? (
                  data.schedule_time
                ) : (
                  "TBD"
                )
              ) : (
                <>&nbsp;</>
              )}
            </i>
          </span>
        </div>

        <div className="team-close-container col-md-9">
          {inputValues.team_1_of_type &&
            inputValues.team_1_of_type_value &&
            !data.isFront && (
              <div className="team-close">
                <button
                  type="button"
                  onClick={() => onRemoveValue(1)}
                  className="close"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            )}
          <input
            type="text"
            name="team_1_of_type"
            className={`form-control ${
              !data.team_1_cancelled ? "cancelled-team" : ""
            }`}
            value={
              data.isFront && data.hideSeed
                ? team1Value
                : inputValues.team_1_of_type_label
            }
            id={`team_1_of_type-${id}`}
            readOnly={
              inputValues.team_1_of_type &&
              inputValues.team_1_of_type_value &&
              data.team_1_of_type != "custom"
            }
            onChange={(e) => onCustomValue(e, 1)}
            disabled={data.isFront}
            autoComplete="off"
          />
          {((data.isFront && data.hideSeed && team1Value.length > 22) ||
            (!data.isFront &&
              inputValues.team_1_of_type_label.length > 22)) && (
            <span className="textContent">
              {data.isFront ? team1Value : inputValues.team_1_of_type_label}

              {!data.isFront && (
                <div className="team-close">
                  <button
                    type="button"
                    onClick={() => onRemoveValue(1)}
                    className="close"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}
            </span>
          )}
        </div>
        <div className="col-md-3 score-div">
          <input
            type="text"
            className={`form-control text-center ${
              data.team_1_score > data.team_2_score ? "text-bold" : ""
            }`}
            value={String(data.team_1_score ?? "00").padStart(2, "0")}
            readOnly
          />
        </div>

        <div className="team-close-container col-md-9">
          {inputValues.team_2_of_type &&
            inputValues.team_2_of_type_value &&
            !data.isFront && (
              <div className="team-close">
                <button
                  type="button"
                  onClick={() => onRemoveValue(2)}
                  className="close"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            )}

          <input
            type="text"
            name="team_2_of_type"
            className={`form-control ${
              !data.team_2_cancelled ? "cancelled-team" : ""
            }`}
            value={
              data.isFront && data.hideSeed
                ? team2Value
                : inputValues.team_2_of_type_label
            }
            id={`team_2_of_type-${id}`}
            readOnly={
              inputValues.team_2_of_type &&
              inputValues.team_2_of_type_value &&
              data.team_2_of_type != "custom"
            }
            onChange={(e) => onCustomValue(e, 2)}
            disabled={data.isFront}
            autoComplete="off"
          />

          {((data.isFront && data.hideSeed && team2Value.length > 22) ||
            (!data.isFront &&
              inputValues.team_2_of_type_label.length > 22)) && (
            <span className="textContent">
              {data.isFront ? team2Value : inputValues.team_2_of_type_label}

              {!data.isFront && (
                <div className="team-close">
                  <button
                    type="button"
                    onClick={() => onRemoveValue(2)}
                    className="close"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              )}
            </span>
          )}
        </div>
        <div className="col-md-3 score-div">
          <input
            type="text"
            className={`form-control text-center ${
              data.team_1_score > data.team_2_score ? "text-bold" : ""
            }`}
            value={String(data.team_2_score ?? "00").padStart(2, "0")}
            readOnly
          />
        </div>

        {data.hasWorkingTeam && (
          <>
            <div className="team-close-container col-md-9">
              {inputValues.team_3_of_type &&
                inputValues.team_3_of_type_value &&
                !data.isFront && (
                  <div className="team-close">
                    <button
                      type="button"
                      onClick={() => onRemoveValue(3)}
                      className="close"
                      aria-label="Close"
                    >
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </div>
                )}

              <input
                type="text"
                name="team_3_of_type"
                className={`form-control team_3_of_type ${
                  !data.team_3_cancelled ? "cancelled-team" : ""
                }`}
                value={inputValues.team_3_of_type_label}
                id={`team_3_of_type-${id}`}
                readOnly={
                  inputValues.team_3_of_type &&
                  inputValues.team_3_of_type_value &&
                  data.team_3_of_type != "custom"
                }
                onChange={(e) => onCustomValue(e, 3)}
                disabled={data.isFront}
                autoComplete="off"
              />

              {((data.isFront && data.hideSeed) ||
                (!data.isFront &&
                  inputValues.team_3_of_type_label.length > 22)) && (
                <span className="textContent">
                  {inputValues.team_3_of_type_label}

                  {!data.isFront && (
                    <div className="team-close">
                      <button
                        type="button"
                        onClick={() => onRemoveValue(3)}
                        className="close"
                        aria-label="Close"
                      >
                        <span aria-hidden="true">&times;</span>
                      </button>
                    </div>
                  )}
                </span>
              )}
            </div>
            <div className="col-md-3 score-div whistle-div"></div>
          </>
        )}
      </div>

      <Handle
        type="source"
        id="source-a"
        position={Position.Left}
        onConnect={onEdgeConnect}
      />
      <Handle
        type="source"
        id="source-b"
        position={Position.Right}
        onConnect={onEdgeConnect}
      />
    </div>
  );
};

export default memo(CompassNode);
